/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import sendPosthogEvent from '@web/utils/send-posthog-event'
import Image from 'next/image'
import { Text } from '@hashicorp/mds-react/components'
import { ExpandableTags, type ILinkTag } from '../expandable-tags'
import s from './style.module.css'

export interface IBlogCard {
	href: string
	image: IImage
	label: string
	title: string
	authors: {
		name: string
		photo?: IImage
	}[]
	detailsPrimary: string
	detailsSecondary: string
	tags: ILinkTag[]
	trackingSource?: string
}

interface IImage {
	url: string
	alt: string
	width: number
	height: number
}

export const BlogCard = ({
	href,
	image,
	label,
	title,
	detailsPrimary,
	detailsSecondary,
	authors,
	tags,
	trackingSource,
}: IBlogCard) => {
	const handleClick = () => {
		sendPosthogEvent('Blog Card Clicked', {
			blog_slug: href,
			blog_title: title,
			category: label,
		})
		if (trackingSource) {
			sendPosthogEvent('Blog Recommendation Clicked', {
				blog_slug: href,
				blog_title: title,
				category: label,
			})
		}
	}
	return (
		<div className={s.root}>
			<a
				href={href}
				className={s.link}
				aria-label={title}
				onClick={handleClick}
			>
				<div className={s.image}>
					<Image
						src={image.url}
						width={image.width}
						height={image.height}
						alt={image.alt ?? ''}
					/>
				</div>
				<div className={s.info}>
					<Text.Label className={s.category} color="faint" weight="medium">
						{label}
					</Text.Label>
					<Text.DisplayExpressive
						className={s.title}
						size="200"
						color="strong"
						weight="semibold"
					>
						{title}
					</Text.DisplayExpressive>
					<div className={s.metadata}>
						<ul className={s.authors}>
							{authors.map((author, index) => {
								return (
									<li key={index} className={s.author}>
										{author.photo ? (
											<Image
												src={author.photo.url}
												width={author.photo.width}
												height={author.photo.height}
												alt={author.photo.alt ?? ''}
											/>
										) : (
											<Image
												src={
													'https://www.datocms-assets.com/2885/1753314445-frame.png'
												}
												width={200}
												height={200}
												alt={'Avatar image of blog post author'}
											/>
										)}
									</li>
								)
							})}
						</ul>
						<div className={s.metadataText}>
							<Text.Body color="primary" weight="medium" size="200">
								{detailsPrimary} |
							</Text.Body>
							<Text.Body color="faint" weight="medium" size="200">
								{detailsSecondary}
							</Text.Body>
						</div>
					</div>
				</div>
			</a>
			<ExpandableTags tags={tags} />
		</div>
	)
}
